import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { formatValue, getSourceColor } from '../utils/dataUtils';

const ResultCard = ({ user, index }) => {
  // Extract the most important fields for display
  const displayName = user.first_name || user.last_name 
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : user.username || 'Unknown User';
  
  const source = user.source || 'Unknown Source';
  const sourceColor = getSourceColor(source);
  
  // Get the first letter for the avatar
  const avatarLetter = displayName.charAt(0).toUpperCase();
  
  // Determine which fields to show in the preview
  const getPreviewFields = () => {
    const fields = [];
    
    // Add email if available
    if (user.email) {
      fields.push({
        name: 'email',
        value: user.email,
        icon: 'email',
      });
    }
    
    // Add mobile if available
    if (user.mobile_number) {
      fields.push({
        name: 'mobile',
        value: user.mobile_number,
        icon: 'phone',
      });
    }
    
    // Add city if available
    if (user.city) {
      fields.push({
        name: 'city',
        value: user.city,
        icon: 'location_city',
      });
    }
    
    // Add any other important field if the above are not available
    if (fields.length < 2 && user.dob) {
      fields.push({
        name: 'dob',
        value: user.dob,
        icon: 'cake',
      });
    }
    
    if (fields.length < 2 && user.gender) {
      fields.push({
        name: 'gender',
        value: user.gender,
        icon: 'wc',
      });
    }
    
    return fields;
  };
  
  const previewFields = getPreviewFields();
  
  // Render a field with icon and label
  const renderField = (field) => {
    return (
      <Box sx={{ display: 'flex', mb: 2 }}>
        {/* Icon wrapper */}
        <Box 
          sx={{ 
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: `${sourceColor}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            flexShrink: 0,
            color: sourceColor
          }}
        >
          {/* Material Icon as span */}
          <span className="material-icons" style={{ fontSize: '16px', lineHeight: 1 }}>
            {field.icon}
          </span>
        </Box>
        
        {/* Field value */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{
            mt: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 'calc(100% - 40px)'
          }}
        >
          {formatValue(field.value)}
        </Typography>
      </Box>
    );
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
    >
      <Card 
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '5px',
            height: '100%',
            backgroundColor: sourceColor,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
          }
        }}
      >
        <Box 
          sx={{ 
            position: 'absolute', 
            top: -15, 
            right: 20,
            zIndex: 1,
          }}
        >
          <Chip
            label={source}
            size="small"
            sx={{
              backgroundColor: sourceColor,
              color: '#fff',
              fontWeight: 'bold',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            }}
          />
        </Box>
        
        <CardContent sx={{ flexGrow: 1, pt: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: sourceColor,
                mr: 2,
                width: 50,
                height: 50,
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
              }}
            >
              {avatarLetter}
            </Avatar>
            <Typography 
              variant="h6" 
              component="h2" 
              noWrap
              sx={{
                maxWidth: 'calc(100% - 70px)', // Account for avatar width and margin
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {displayName}
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ px: 0.5 }}>
            {previewFields.map((field, i) => (
              <Box key={i}>
                {renderField(field)}
              </Box>
            ))}
            
            {previewFields.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center">
                Limited information available
              </Typography>
            )}
          </Box>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
          <Button
            component={Link}
            to={`/details/${user._id}`}
            variant="outlined"
            size="small"
            endIcon={<VisibilityIcon />}
            sx={{
              borderColor: sourceColor,
              color: sourceColor,
              '&:hover': {
                borderColor: sourceColor,
                backgroundColor: `${sourceColor}10`,
              }
            }}
          >
            View Details
          </Button>
        </CardActions>
      </Card>
    </motion.div>
  );
};

export default ResultCard; 